import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";

export default function AdminApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [auth, setAuth] = useState<boolean | null>(null);
  const [booting, setBooting] = useState(true);
  const [loginErr, setLoginErr] = useState<string | null>(null);
  const [surveys, setSurveys] = useState<{ id: string; name: string }[]>([]);
  const surveyId = searchParams.get("survey_id") ?? "";
  const location = useLocation();
  const isPainDiscoveryPath =
    location.pathname === "/admin" || location.pathname.endsWith("/admin/pain-discovery");

  const probe = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/stats?range=1m", { credentials: "include" });
      if (res.status === 401) {
        setAuth(false);
        return;
      }
      setAuth(res.ok);
    } catch {
      setAuth(false);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    void probe();
  }, [probe]);

  useEffect(() => {
    async function loadSurveys() {
      if (!auth) return;
      const res = await fetch("/api/admin/surveys", { credentials: "include" });
      if (!res.ok) return;
      const data = (await res.json()) as { items: { id: string; name: string }[] };
      setSurveys(data.items ?? []);
    }
    void loadSurveys();
  }, [auth]);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    setLoginErr(null);
    const res = await fetch("/api/admin/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      setLoginErr("Clave incorrecta.");
      return;
    }
    setPassword("");
    setAuth(true);
  }

  async function logout() {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" });
    setAuth(false);
  }

  if (booting) {
    return (
      <main className="pp-shell flex items-center justify-center p-4">
        <p className="text-sm text-pp-muted">Cargando…</p>
      </main>
    );
  }

  if (auth === false) {
    return (
      <main className="pp-shell flex flex-col items-center justify-center p-6">
        <div className="pp-card w-full max-w-md p-8 shadow-ppCard">
          <div className="mb-6 flex justify-center">
            <Logo variant="full" iconSize={48} />
          </div>
          <p className="pp-badge-soft mb-2 w-fit">Acceso interno</p>
          <h1 className="text-xl font-bold text-pp-main">Panel admin</h1>
          <p className="mt-1 text-sm text-pp-muted">Ingresá con la clave del workspace.</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <label className="block text-sm font-medium text-pp-main">
              Clave
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pp-input mt-2"
              />
            </label>
            {loginErr && (
              <p className="text-sm text-rose-600" role="alert">
                {loginErr}
              </p>
            )}
            <button type="submit" className="pp-btn-primary w-full">
              Entrar
            </button>
          </form>
        </div>
      </main>
    );
  }

  const navCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? "pp-nav-pill-active" : "pp-nav-pill";

  const legacyNavCls = ({ isActive }: { isActive: boolean }) =>
    `block w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
      isActive ? "bg-pp-gradient text-white font-semibold" : "text-pp-main hover:bg-pp-soft"
    }`;

  const navTo = (path: string) => (surveyId ? `${path}?survey_id=${encodeURIComponent(surveyId)}` : path);

  return (
    <main className="pp-shell p-4 pb-24 max-w-5xl mx-auto">
      <header className="pp-card mb-6 p-4 shadow-ppCard">
        <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <Logo variant="full" iconSize={36} className="min-w-0" />
            <span className="hidden sm:inline h-6 w-px shrink-0 bg-pp-border" aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-wider text-pp-quiet">Admin</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="pp-link text-sm" onClick={() => void logout()}>
              Salir
            </button>
          </div>
        </div>
        <nav className="mt-4 flex flex-wrap items-center gap-1 pp-nav-pills p-1" aria-label="Secciones">
          <NavLink
            to={navTo("/admin")}
            end
            className={({ isActive }) =>
              isActive || isPainDiscoveryPath ? "pp-nav-pill-active" : "pp-nav-pill"
            }
          >
            Dashboard
          </NavLink>
          <NavLink to={navTo("/admin/responses")} className={navCls}>
            Respuestas
          </NavLink>
          <NavLink to={navTo("/admin/analysis")} className={navCls}>
            Análisis
          </NavLink>
          <details className="group relative">
            <summary
              className="pp-nav-pill cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden"
              aria-label="Vistas anteriores"
            >
              Legacy
            </summary>
            <div
              className="absolute left-0 top-full z-20 mt-1 min-w-[12rem] rounded-xl border border-pp-border bg-pp-card py-1 shadow-ppCard"
              role="menu"
            >
              <NavLink to={navTo("/admin/legacy")} className={legacyNavCls} role="menuitem">
                Dashboard clásico
              </NavLink>
              <NavLink to={navTo("/admin/new-dash")} className={legacyNavCls} role="menuitem">
                Validation Score
              </NavLink>
            </div>
          </details>
          <NavLink to={navTo("/admin/guide")} className={navCls}>
            Guía
          </NavLink>
          <NavLink to={navTo("/admin/surveys")} className={navCls}>
            Encuestas
          </NavLink>
          <div className="ml-auto flex flex-wrap items-center gap-2 rounded-xl border border-pp-border bg-pp-soft px-2 py-1.5">
            <span className="text-xs font-medium text-pp-muted whitespace-nowrap">Encuesta</span>
            <select
              value={surveyId}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set("survey_id", e.target.value);
                else next.delete("survey_id");
                setSearchParams(next);
              }}
              className="pp-input-field min-w-[10rem]"
            >
              <option value="">Todas</option>
              {surveys.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </nav>
      </header>
      <Outlet />
    </main>
  );
}
